import i18n from "@modules/shared/localization/i18n";
import {
  ALL_PRODUCTS_COLLECTION,
  PRODUCTS_PAGE_SIZE,
  ProductsPage,
} from "@modules/shop/components/ProductsPage";
import { resolveCollectionSortOption } from "@modules/shop/components/Products/sort-options";
import {
  collectionInfiniteQueryOptions,
  productTypesQueryOptions,
} from "@modules/shopify/hooks";
import {
  buildProductFilters,
  filterParsers,
} from "@modules/shop/components/Products/filter-params";
import { createFileRoute } from "@tanstack/react-router";
import { seo } from "@utils/seo";

export const Route = createFileRoute(
  "/$locale/_layout/(public)/_public/products",
)({
  component: ProductsRoute,
  validateSearch: (search: Record<string, unknown>) => {
    const sort = typeof search.sort === "string" ? search.sort : undefined;
    return sort && sort !== "default" ? { sort } : {};
  },
  ssr: true,
  loaderDeps: ({ search }) => ({
    sort: search.sort,
  }),
  loader: async ({ context: { queryClient, country, language }, deps }) => {
    const { sortKey, reverse } = resolveCollectionSortOption(deps.sort);

    const defaultFilters = {
      type: filterParsers.type.defaultValue,
      color: filterParsers.color.defaultValue,
      size: filterParsers.size.defaultValue,
      minPrice: filterParsers.minPrice.defaultValue,
      maxPrice: filterParsers.maxPrice.defaultValue,
      gender: filterParsers.gender.defaultValue,
    };

    await Promise.all([
      queryClient.ensureInfiniteQueryData(
        collectionInfiniteQueryOptions({
          handle: ALL_PRODUCTS_COLLECTION,
          numberOfProducts: PRODUCTS_PAGE_SIZE,
          productFilters: buildProductFilters(defaultFilters),
          country,
          language,
          sortKey,
          reverse,
        }),
      ),
      queryClient.ensureQueryData(productTypesQueryOptions()),
    ]);
  },
  headers: () => ({
    "Cache-Control":
      "public, max-age=300, s-maxage=300, stale-while-revalidate=86400",
  }),
  staleTime: 60_000,
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    return {
      meta: [
        ...seo({
          title: t("Seo:products.title"),
          description: t("Seo:products.description"),
        }),
      ],
    };
  },
});

function ProductsRoute() {
  const { locale } = Route.useParams();
  return <ProductsPage locale={locale} />;
}
